import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { Loader2, Upload, Trash2, Megaphone, Save } from "lucide-react";
import { api } from "@/lib/api";
import { invalidateHomepageConfig } from "@/lib/homepageConfigCache";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";

/** Preset library for the button styling section. Each preset is a chip
 *  that instantly applies a coherent color palette to the ad's CTA button. */
const BUTTON_PRESETS = [
  { id: "yellow-alert", name: "Yellow Alert", style: { button_bg_color: "#FFEB3B", button_text_color: "#000000", button_border_color: "#000000", button_radius: 999, button_shadow: true,  button_size: "md" } },
  { id: "pink-pop",     name: "Pink Pop",     style: { button_bg_color: "#EC4899", button_text_color: "#FFFFFF", button_border_color: "#000000", button_radius: 999, button_shadow: true,  button_size: "md" } },
  { id: "neon-cyan",    name: "Neon Cyan",    style: { button_bg_color: "#22D3EE", button_text_color: "#000000", button_border_color: "#000000", button_radius: 999, button_shadow: true,  button_size: "md" } },
  { id: "mint-fresh",   name: "Mint Fresh",   style: { button_bg_color: "#84CC16", button_text_color: "#000000", button_border_color: "#000000", button_radius: 12,  button_shadow: true,  button_size: "md" } },
  { id: "red-urgent",   name: "Red Urgent",   style: { button_bg_color: "#EF4444", button_text_color: "#FFFFFF", button_border_color: "#000000", button_radius: 999, button_shadow: true,  button_size: "lg" } },
  { id: "elegant-dark", name: "Elegant Dark", style: { button_bg_color: "#0F172A", button_text_color: "#FFFFFF", button_border_color: "#0F172A", button_radius: 8,   button_shadow: false, button_size: "md" } },
  { id: "purple-pop",   name: "Purple Pop",   style: { button_bg_color: "#8B5CF6", button_text_color: "#FFFFFF", button_border_color: "#000000", button_radius: 999, button_shadow: true,  button_size: "md" } },
  { id: "ghost",        name: "Ghost",        style: { button_bg_color: "#FFFFFF", button_text_color: "#000000", button_border_color: "#000000", button_radius: 999, button_shadow: false, button_size: "md" } },
];

const ANIMATIONS = [
  { v: "none",      l: "Nenhuma" },
  { v: "float",     l: "Flutuar (recomendado)" },
  { v: "pulse",     l: "Pulsar" },
  { v: "bounce",    l: "Saltar" },
  { v: "shake",     l: "Tremer" },
  { v: "blink",     l: "Piscar" },
  { v: "slideY",    l: "Deslizar Y" },
  { v: "glow",      l: "Brilho" },
  { v: "wobble",    l: "Oscilar (wobble)" },
  { v: "swing",     l: "Balouço (swing)" },
  { v: "heartbeat", l: "Batimento cardíaco" },
  { v: "flip",      l: "Virar 360° (flip Y)" },
  { v: "tilt",      l: "Inclinar (tilt)" },
  { v: "zoom",      l: "Zoom in/out" },
  { v: "spin",      l: "Rodar 360° contínuo" },
  { v: "rubber",    l: "Elástico (rubber band)" },
  { v: "jello",     l: "Gelatina (jello)" },
];

const SIDE_LABEL = { left: "Esquerda", right: "Direita" };

export default function AdsManager() {
  const [cfg, setCfg] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/homepage-config");
      setCfg(data);
    } catch (e) {
      toast.error("Erro ao carregar configuração.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const patchAds = (patch) => setCfg((c) => ({ ...c, ads: { ...(c?.ads || {}), ...patch } }));
  const patchSide = (side, patch) => setCfg((c) => ({
    ...c,
    ads: {
      ...(c?.ads || {}),
      [side]: { ...((c?.ads || {})[side] || {}), ...patch },
    },
  }));

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      delete payload._id;
      await api.put("/homepage-config", payload);
      // Invalidate the shared homepage cache so the public site fetches the
      // fresh `ads` payload on the next visit (otherwise SideAds keeps
      // rendering the stale cached config without the new values).
      invalidateHomepageConfig();
      toast.success("Publicidade guardada.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erro ao guardar.");
    } finally {
      setSaving(false);
    }
  };

  if (loading || !cfg) {
    return <div className="p-8 text-slate-400 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> A carregar…</div>;
  }
  const ads = cfg.ads || {};

  return (
    <div className="max-w-6xl mx-auto space-y-6" data-testid="ads-manager">
      <header>
        <h1 className="font-[Fredoka] text-3xl font-bold text-slate-900 flex items-center gap-2">
          <Megaphone className="w-7 h-7 text-pink-500" /> Publicidade lateral
        </h1>
        <p className="text-slate-500 text-sm mt-1">
          Dois blocos independentes fixos nas laterais do site (só em desktop ≥1024px). Cada bloco tem imagem PNG, cabeçalho, botão personalizável e opções de animação/ciclo.
        </p>
      </header>

      <div className="bg-white rounded-2xl border border-slate-200 p-4 flex items-center justify-between shadow-sm">
        <label className="flex items-center gap-3 cursor-pointer">
          <input
            type="checkbox"
            className="w-5 h-5"
            checked={!!ads.enabled}
            onChange={(e) => patchAds({ enabled: e.target.checked })}
            data-testid="ads-global-enabled"
          />
          <div>
            <div className="font-bold text-slate-900">Ativar publicidade globalmente</div>
            <div className="text-xs text-slate-500">Quando desligado, nenhum bloco aparece — mesmo que individualmente activados.</div>
          </div>
        </label>
        <Button onClick={save} disabled={saving} data-testid="ads-save" className="bg-emerald-600 hover:bg-emerald-700 text-white font-bold rounded-full">
          {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />}
          Guardar
        </Button>
      </div>

      <div className="grid md:grid-cols-2 gap-6">
        {["left", "right"].map((side) => (
          <SidePanel
            key={side}
            side={side}
            ad={ads[side] || {}}
            onPatch={(p) => patchSide(side, p)}
          />
        ))}
      </div>
    </div>
  );
}

function SidePanel({ side, ad, onPatch }) {
  return (
    <div className="bg-white rounded-2xl border-2 border-slate-200 shadow-sm p-5 space-y-4" data-testid={`ads-panel-${side}`}>
      <div className="flex items-center justify-between">
        <h2 className="font-[Fredoka] text-lg font-bold text-slate-900">
          Publicidade {SIDE_LABEL[side]}
        </h2>
        <label className="flex items-center gap-2 text-xs cursor-pointer">
          <input
            type="checkbox"
            checked={!!ad.enabled}
            onChange={(e) => onPatch({ enabled: e.target.checked })}
            data-testid={`ads-${side}-enabled`}
          />
          <span className="font-bold">Ativa</span>
        </label>
      </div>

      <ImageUploaderPerLang side={side} ad={ad} onPatch={onPatch} />
      <MultiLang label="Cabeçalho (texto acima da imagem)" field="header" side={side} ad={ad} onPatch={onPatch} />
      <MultiLang label="Texto do botão" field="button_label" side={side} ad={ad} onPatch={onPatch} />

      <div>
        <Label className="text-xs font-bold uppercase">URL do botão (por idioma)</Label>
        <div className="grid grid-cols-3 gap-2 mt-1">
          {["pt", "en", "es"].map((lg) => (
            <Input
              key={lg}
              value={ad[`button_url_${lg}`] || ""}
              onChange={(e) => onPatch({ [`button_url_${lg}`]: e.target.value })}
              placeholder={`URL ${lg.toUpperCase()} (/loja ou https://…)`}
              data-testid={`ads-${side}-btn-url-${lg}`}
            />
          ))}
        </div>
        <p className="text-[10px] text-slate-500 mt-1">Se um idioma ficar sem URL, é usado o URL PT nesse idioma (só o URL — se a imagem estiver em branco, o anúncio não aparece).</p>
      </div>

      {/* Estilo do cabeçalho — mesmo formato do botão */}
      <div className="pt-3 border-t border-slate-100 space-y-3">
        <div className="font-bold text-xs uppercase tracking-wider text-slate-700">🏷️ Estilo do cabeçalho</div>
        <div className="grid grid-cols-3 gap-2">
          <ColorField label="Fundo" side={side} k="header_bg" ad={ad} onPatch={onPatch} />
          <ColorField label="Texto" side={side} k="header_text" ad={ad} onPatch={onPatch} />
          <ColorField label="Bordo" side={side} k="header_border" ad={ad} onPatch={onPatch} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-[10px] uppercase font-bold">Tamanho</Label>
            <Select value={ad.header_size || "sm"} onValueChange={(v) => onPatch({ header_size: v })}>
              <SelectTrigger data-testid={`ads-${side}-header-size`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sm">Pequeno</SelectItem>
                <SelectItem value="md">Médio</SelectItem>
                <SelectItem value="lg">Grande</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Raio (0-999)</Label>
            <Input
              type="number"
              min={0}
              max={999}
              value={ad.header_radius ?? 999}
              onChange={(e) => onPatch({ header_radius: Number(e.target.value) })}
              data-testid={`ads-${side}-header-radius`}
            />
          </div>
          <label className="flex items-end gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4"
              checked={!!ad.header_shadow}
              onChange={(e) => onPatch({ header_shadow: e.target.checked })}
              data-testid={`ads-${side}-header-shadow`}
            />
            <span className="text-xs font-bold pb-1">Sombra 3D</span>
          </label>
        </div>
        <HeaderPreview side={side} ad={ad} />
      </div>

      {/* Estilo do botão */}
      <div className="pt-3 border-t border-slate-100 space-y-3">
        <div className="font-bold text-xs uppercase tracking-wider text-slate-700">🎨 Estilo do botão</div>
        <PresetChips side={side} onPatch={onPatch} />
        <div className="grid grid-cols-3 gap-2">
          <ColorField label="Fundo" side={side} k="bg" ad={ad} onPatch={onPatch} />
          <ColorField label="Texto" side={side} k="text" ad={ad} onPatch={onPatch} />
          <ColorField label="Bordo" side={side} k="border" ad={ad} onPatch={onPatch} />
        </div>
        <div className="grid grid-cols-3 gap-2">
          <div>
            <Label className="text-[10px] uppercase font-bold">Tamanho</Label>
            <Select value={ad.button_size || "md"} onValueChange={(v) => onPatch({ button_size: v })}>
              <SelectTrigger data-testid={`ads-${side}-btn-size`}><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="sm">Pequeno</SelectItem>
                <SelectItem value="md">Médio</SelectItem>
                <SelectItem value="lg">Grande</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Raio (0-999)</Label>
            <Input
              type="number"
              min={0}
              max={999}
              value={ad.button_radius ?? 999}
              onChange={(e) => onPatch({ button_radius: Number(e.target.value) })}
              data-testid={`ads-${side}-btn-radius`}
            />
          </div>
          <label className="flex items-end gap-2 cursor-pointer">
            <input
              type="checkbox"
              className="w-4 h-4"
              checked={!!ad.button_shadow}
              onChange={(e) => onPatch({ button_shadow: e.target.checked })}
              data-testid={`ads-${side}-btn-shadow`}
            />
            <span className="text-xs font-bold pb-1">Sombra 3D</span>
          </label>
        </div>
        <ButtonPreview side={side} ad={ad} />
      </div>

      {/* Espaçamentos verticais */}
      <div className="pt-3 border-t border-slate-100 space-y-2">
        <div className="font-bold text-xs uppercase tracking-wider text-slate-700">📏 Posição vertical</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase font-bold">Distância cabeçalho → imagem (px)</Label>
            <Input
              type="number"
              min={0}
              value={ad.header_gap ?? 8}
              onChange={(e) => onPatch({ header_gap: Number(e.target.value) })}
              data-testid={`ads-${side}-header-gap`}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Distância imagem → botão (px)</Label>
            <Input
              type="number"
              min={0}
              value={ad.button_gap ?? 8}
              onChange={(e) => onPatch({ button_gap: Number(e.target.value) })}
              data-testid={`ads-${side}-button-gap`}
            />
          </div>
        </div>
      </div>

      {/* Animação + Timing */}
      <div className="pt-3 border-t border-slate-100 space-y-3">
        <div className="font-bold text-xs uppercase tracking-wider text-slate-700">🎬 Animação e ciclo</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase font-bold">Animação</Label>
            <Select value={ad.animation || "float"} onValueChange={(v) => onPatch({ animation: v })}>
              <SelectTrigger data-testid={`ads-${side}-animation`}><SelectValue /></SelectTrigger>
              <SelectContent>
                {ANIMATIONS.map((a) => <SelectItem key={a.v} value={a.v}>{a.l}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Largura (px)</Label>
            <Input
              type="number"
              value={ad.width ?? 160}
              onChange={(e) => onPatch({ width: Number(e.target.value) })}
              data-testid={`ads-${side}-width`}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Topo (px)</Label>
            <Input
              type="number"
              value={ad.top_offset ?? 120}
              onChange={(e) => onPatch({ top_offset: Number(e.target.value) })}
              data-testid={`ads-${side}-top`}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">z-index</Label>
            <Input
              type="number"
              value={ad.z_index ?? 40}
              onChange={(e) => onPatch({ z_index: Number(e.target.value) })}
            />
          </div>
        </div>
        <div className="grid grid-cols-3 gap-2">
          <SecondsField label="Atraso inicial (s)" field="delay_ms"   side={side} ad={ad} onPatch={onPatch} testid={`ads-${side}-delay`} />
          <SecondsField label="Visível durante (s)" field="visible_ms" side={side} ad={ad} onPatch={onPatch} testid={`ads-${side}-visible`} />
          <HiddenSecondsField side={side} ad={ad} onPatch={onPatch} />
        </div>
        <p className="text-[11px] text-slate-500">
          "Depois esconder durante 0 s" mantém o anúncio sempre visível. Qualquer valor &gt;0 activa o ciclo aparecer/desaparecer.
        </p>
      </div>

      {/* Perspectiva 3D */}
      <div className="pt-3 border-t border-slate-100 space-y-2">
        <div className="font-bold text-xs uppercase tracking-wider text-slate-700">🎥 Perspectiva 3D</div>
        <div className="grid grid-cols-2 gap-2">
          <div>
            <Label className="text-[10px] uppercase font-bold">Profundidade (px, 0=desligado)</Label>
            <Input
              type="number"
              min={0}
              max={3000}
              value={ad.perspective_depth ?? 0}
              onChange={(e) => onPatch({ perspective_depth: Number(e.target.value) })}
              data-testid={`ads-${side}-perspective`}
            />
            <p className="text-[10px] text-slate-400 mt-0.5">400–1200 é o intervalo natural.</p>
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Rotação X (deg — frente/trás)</Label>
            <Input
              type="number"
              min={-90}
              max={90}
              value={ad.rotate_x ?? 0}
              onChange={(e) => onPatch({ rotate_x: Number(e.target.value) })}
              data-testid={`ads-${side}-rotate-x`}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Rotação Y (deg — lado/lado)</Label>
            <Input
              type="number"
              min={-90}
              max={90}
              value={ad.rotate_y ?? 0}
              onChange={(e) => onPatch({ rotate_y: Number(e.target.value) })}
              data-testid={`ads-${side}-rotate-y`}
            />
          </div>
          <div>
            <Label className="text-[10px] uppercase font-bold">Rotação Z (deg — no plano)</Label>
            <Input
              type="number"
              min={-180}
              max={180}
              value={ad.rotate_z ?? 0}
              onChange={(e) => onPatch({ rotate_z: Number(e.target.value) })}
              data-testid={`ads-${side}-rotate-z`}
            />
          </div>
        </div>
        <p className="text-[11px] text-slate-500">
          Ex.: Profundidade 800 + Y=-15° inclina o anúncio para dentro do ecrã como uma capa 3D. Rotação Z=-8° dá um look "estilo poster torto".
        </p>
      </div>
    </div>
  );
}

/** Per-language uploader — three compact rows (PT/EN/ES) each with its
 *  own PNG upload button and URL input. If a language is left blank, the
 *  frontend falls back to PT (and PT falls back to the legacy `image_url`). */
function ImageUploaderPerLang({ side, ad, onPatch }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase">Imagem por idioma (PNG transparente)</Label>
      <div className="space-y-2 mt-1">
        {["pt", "en", "es"].map((lg) => (
          <LangImageRow key={lg} side={side} lang={lg} ad={ad} onPatch={onPatch} />
        ))}
      </div>
      <p className="text-[10px] text-slate-500 mt-1">
        Se um idioma ficar sem imagem, o anúncio <strong>não aparece</strong> nesse idioma.
      </p>
    </div>
  );
}

function LangImageRow({ side, lang, ad, onPatch }) {
  const [uploading, setUploading] = useState(false);
  const key = `image_url_${lang}`;
  const url = ad[key] || "";
  const onFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.type !== "image/png") {
      toast.error("Só é permitido PNG (transparente).");
      e.target.value = "";
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/homepage-config/upload-image?kind=ad_${side}_${lang}`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      onPatch({ [key]: data.url });
      toast.success(`Imagem ${lang.toUpperCase()} carregada.`);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro no upload.");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };
  return (
    <div className="flex items-center gap-2">
      <div
        className="w-12 h-12 rounded-lg border border-slate-200 shrink-0 grid place-items-center bg-slate-50"
        style={{
          backgroundImage:
            "linear-gradient(45deg,#e2e8f0 25%,transparent 25%),linear-gradient(-45deg,#e2e8f0 25%,transparent 25%),linear-gradient(45deg,transparent 75%,#e2e8f0 75%),linear-gradient(-45deg,transparent 75%,#e2e8f0 75%)",
          backgroundSize: "8px 8px",
          backgroundPosition: "0 0, 0 4px, 4px -4px, -4px 0",
        }}
        data-testid={`ads-${side}-preview-${lang}`}
      >
        {url ? (
          <img src={url} alt="" className="max-w-full max-h-full object-contain" />
        ) : (
          <span className="text-[8px] font-bold text-slate-400">{lang.toUpperCase()}</span>
        )}
      </div>
      <span className="text-[10px] font-bold uppercase text-slate-500 w-6 shrink-0">{lang}</span>
      <Input
        value={url}
        onChange={(e) => onPatch({ [key]: e.target.value })}
        placeholder={`URL da imagem ${lang.toUpperCase()}`}
        className="flex-1 h-8 text-xs"
        data-testid={`ads-${side}-image-url-${lang}`}
      />
      <label className="inline-flex items-center gap-1 px-2 py-1 text-[10px] font-bold rounded-full bg-slate-800 text-white hover:bg-slate-900 cursor-pointer shrink-0">
        {uploading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Upload className="w-3 h-3" />}
        PNG
        <input
          type="file"
          accept="image/png"
          className="hidden"
          onChange={onFile}
          data-testid={`ads-${side}-upload-${lang}`}
        />
      </label>
      {url && (
        <button
          type="button"
          onClick={() => onPatch({ [key]: "" })}
          className="text-rose-500 hover:text-rose-700 shrink-0"
          data-testid={`ads-${side}-clear-image-${lang}`}
          title="Limpar"
        >
          <Trash2 className="w-3.5 h-3.5" />
        </button>
      )}
    </div>
  );
}

function MultiLang({ label, field, side, ad, onPatch }) {
  return (
    <div>
      <Label className="text-xs font-bold uppercase">{label}</Label>
      <div className="grid grid-cols-3 gap-2 mt-1">
        {["pt", "en", "es"].map((lang) => (
          <Input
            key={lang}
            value={ad[`${field}_${lang}`] || ""}
            onChange={(e) => onPatch({ [`${field}_${lang}`]: e.target.value })}
            placeholder={lang.toUpperCase()}
            data-testid={`ads-${side}-${field === "button_label" ? "btn-label" : field}-${lang}`}
          />
        ))}
      </div>
    </div>
  );
}

function ColorField({ label, side, k, ad, onPatch }) {
  const map = {
    bg: "button_bg_color", text: "button_text_color", border: "button_border_color",
    header_bg: "header_bg_color", header_text: "header_text_color", header_border: "header_border_color",
  };
  const testidBase = {
    bg: "button-bg-color", text: "button-text-color", border: "button-border-color",
    header_bg: "header-bg-color", header_text: "header-text-color", header_border: "header-border-color",
  }[k];
  const key = map[k];
  return (
    <div>
      <Label className="text-[10px] uppercase font-bold">{label}</Label>
      <div className="flex items-center gap-1">
        <Input
          type="color"
          value={ad[key] || "#000000"}
          onChange={(e) => onPatch({ [key]: e.target.value })}
          className="h-9 p-1 w-12"
          data-testid={`ads-${side}-${testidBase}`}
        />
        <Input
          value={ad[key] || ""}
          onChange={(e) => onPatch({ [key]: e.target.value })}
          className="h-9 flex-1 font-mono text-xs"
          data-testid={`ads-${side}-${testidBase}-hex`}
        />
      </div>
    </div>
  );
}

function PresetChips({ side, onPatch }) {
  return (
    <div className="flex flex-wrap gap-1.5">
      {BUTTON_PRESETS.map((p) => {
        const s = p.style;
        return (
          <button
            key={p.id}
            type="button"
            onClick={() => onPatch(s)}
            className="text-[10px] font-black px-2.5 py-1 uppercase tracking-wide hover:scale-105 transition-transform"
            title={p.name}
            style={{
              backgroundColor: s.button_bg_color,
              color: s.button_text_color,
              border: `2px solid ${s.button_border_color}`,
              borderRadius: `${s.button_radius}px`,
              boxShadow: s.button_shadow ? "0 2px 0 rgba(0,0,0,0.85)" : "none",
            }}
            data-testid={`ads-${side}-preset-${p.id}`}
          >
            {p.name}
          </button>
        );
      })}
    </div>
  );
}

function ButtonPreview({ side, ad }) {
  const sizeCls = { sm: "px-2.5 py-1 text-[10px]", md: "px-3.5 py-1.5 text-xs", lg: "px-5 py-2.5 text-sm" }[ad.button_size || "md"];
  return (
    <div className="pt-2">
      <Label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">Preview</Label>
      <span
        className={`${sizeCls} inline-block font-black uppercase tracking-wider`}
        style={{
          backgroundColor: ad.button_bg_color || "#FFEB3B",
          color: ad.button_text_color || "#000000",
          border: `2px solid ${ad.button_border_color || "#000000"}`,
          borderRadius: `${ad.button_radius ?? 999}px`,
          boxShadow: ad.button_shadow ? "0 4px 0 rgba(0,0,0,0.85)" : "none",
        }}
        data-testid={`ads-${side}-btn-preview`}
      >
        {ad.button_label_pt || "COMPRAR"}
      </span>
    </div>
  );
}

function HeaderPreview({ side, ad }) {
  const sizeCls = { sm: "px-2.5 py-1 text-[10px]", md: "px-3.5 py-1.5 text-xs", lg: "px-5 py-2.5 text-sm" }[ad.header_size || "sm"];
  return (
    <div className="pt-2">
      <Label className="text-[10px] uppercase font-bold text-slate-500 mb-1 block">Preview</Label>
      <span
        className={`${sizeCls} inline-block font-black uppercase tracking-[0.15em]`}
        style={{
          backgroundColor: ad.header_bg_color || "#000000",
          color: ad.header_text_color || "#FFFFFF",
          border: `2px solid ${ad.header_border_color || "#000000"}`,
          borderRadius: `${ad.header_radius ?? 999}px`,
          boxShadow: ad.header_shadow ? "0 4px 0 rgba(0,0,0,0.85)" : "none",
        }}
        data-testid={`ads-${side}-header-preview`}
      >
        {ad.header_pt || "PATROCINADO POR:"}
      </span>
    </div>
  );
}

function SecondsField({ label, field, side, ad, onPatch, testid }) {
  const ms = Number(ad[field] || 0);
  return (
    <div>
      <Label className="text-[10px] uppercase font-bold">{label}</Label>
      <Input
        type="number"
        min={0}
        value={Math.round(ms / 1000)}
        onChange={(e) => onPatch({ [field]: Math.max(0, Number(e.target.value)) * 1000 })}
        data-testid={testid}
      />
    </div>
  );
}

/** Represents `hidden_ms = cycle_ms - visible_ms`. Storing `cycle_ms` in the
 *  backend keeps a single-source-of-truth for the total period. */
function HiddenSecondsField({ side, ad, onPatch }) {
  const visibleMs = Number(ad.visible_ms || 5000);
  const cycleMs = Number(ad.cycle_ms || 0);
  const hiddenSec = cycleMs === 0 ? 0 : Math.max(0, Math.round((cycleMs - visibleMs) / 1000));
  return (
    <div>
      <Label className="text-[10px] uppercase font-bold">Depois esconder (s, 0=sempre)</Label>
      <Input
        type="number"
        min={0}
        value={hiddenSec}
        onChange={(e) => {
          const sec = Math.max(0, Number(e.target.value));
          onPatch({ cycle_ms: sec === 0 ? 0 : visibleMs + sec * 1000 });
        }}
        data-testid={`ads-${side}-cycle`}
      />
    </div>
  );
}
